import axios from 'axios';
import { io } from 'socket.io-client';
import { isPublicRoute } from './constants/publicRoutes';
import { logDevApiError } from './utils/devApiDebug';
import { getDevApiBaseURL, getDevSocketOrigin } from './utils/devApiBaseUrl';

// For staging environment, we need to handle self-signed certificates
const isStaging = window.location.hostname === 'dev.lotwleague.com';

//force rebuild
// Extract base URL logic to avoid nested ternary
const getBaseURL = () => {
  if (isStaging) {
    return 'https://54.161.78.25/api';  // Staging backend for dev environment (self-signed cert - accept in browser)
  }
  if (window.location.hostname === 'lotwleague.com' || window.location.hostname === 'www.lotwleague.com') {
    return 'https://api.lotwleague.com/api';  // Production API URL
  }
  if (process.env.NODE_ENV === 'development') {
    return getDevApiBaseURL();
  }
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  return '/api';
};

const api = axios.create({
  baseURL: getBaseURL(),
  withCredentials: true
});

// State for token refresh
let isRefreshing = false;
let refreshSubscribers = [];

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(new Error(error.message || 'Request interceptor error'))
);

// Response interceptor for token refresh
api.interceptors.response.use(
  response => response,
  async (error) => {
    logDevApiError(error, 'api');
    const originalRequest = error.config;

    // Don't try to refresh token for login requests
    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url?.includes('/users/login')) {
      originalRequest._retry = true;

      try {
        const token = await refreshToken();
        originalRequest.headers['Authorization'] = `Bearer ${token}`;
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        return api(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem('token');
        if (!isPublicRoute(window.location.pathname)) {
          window.location.href = '/login';
        }
        return Promise.reject(new Error(refreshError.message || 'Token refresh failed'));
      }
    }
    // Preserve the original error with response data for better error handling
    if (error.response?.data) {
      return Promise.reject(error);
    }
    return Promise.reject(new Error(error.message || 'Request failed'));
  }
);

// Authentication endpoints
api.login = async (credentials) => {
  // For staging environment, send as form data instead of JSON
  const isStaging = window.location.hostname === 'dev.lotwleague.com';

  let response;
  if (isStaging) {
    // Convert to form data for staging
    const formData = new URLSearchParams();
    formData.append('username', credentials.username);
    formData.append('password', credentials.password);

    response = await api.post('/users/login', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });
  } else {
    // Use JSON for production
    response = await api.post('/users/login', credentials);
  }

  if (response.data.token) {
    localStorage.setItem('token', response.data.token);
    disconnectSocket();
    initSocket();
  }
  return response;
};
api.register = (userData) => api.post('/users/register', userData);
api.getOnboardingInvite = (token) => api.get(`/onboarding/${token}`);
api.completeOnboarding = (token, userData) => api.post(`/onboarding/${token}`, userData);
api.updateOnboardingTour = (status) => api.patch('/users/onboarding-tour', { status });
api.getOnboardingConfig = () => api.get('/users/onboarding-config');

// User profile and settings
api.getProfile = () => api.get('/users/profile');
api.updateSettings = (settings) => api.put('/users/settings', settings);
api.resetPassword = (newPassword) => api.put('/users/reset-password', { password: newPassword });
api.getByeStatus = () => api.get('/users/bye-status');

// Discord verification
api.startDiscordVerification = (discordUsername) => api.post('/users/discord/start-verification', { discordUsername });
api.completeDiscordVerification = (verificationCode) => api.post('/users/discord/complete-verification', { verificationCode });
api.getDiscordVerificationStatus = () => api.get('/users/discord/verification-status');
api.cancelDiscordVerification = () => api.post('/users/discord/cancel-verification');

// SMS verification
api.startSMSVerification = (phoneNumber) => api.post('/users/sms/start-verification', { phoneNumber });
api.completeSMSVerification = (verificationCode) => api.post('/users/sms/complete-verification', { verificationCode });
api.getSMSVerificationStatus = () => api.get('/users/sms/verification-status');
api.cancelSMSVerification = () => api.post('/users/sms/cancel-verification');
api.toggleSMSReminders = (enabled) => api.post('/users/sms/toggle-reminders', { enabled });

// Password reset functionality
api.identifyAccount = (identifier) => api.post('/password-reset/identify', { identifier });
api.initiatePasswordReset = (identifier, channel) => api.post('/password-reset/initiate', { identifier, channel });
api.validateResetToken = (token) => api.get(`/password-reset/validate-token/${token}`);
api.resetPasswordWithToken = (token, newPassword) => api.post('/password-reset/reset', { token, newPassword });
api.getDiscordStatus = () => api.get('/password-reset/discord-status');

// Calendar and games
api.getCalendarStatus = () => api.get('/calendar/status');
api.fetchGames = (week, season) => api.get(`/games?week=${week}&season=${season}`);
api.fetchCalendar = (season) => {
  const url = season ? `/calendar?season=${season}` : '/calendar';
  return api.get(url);
};
api.fetchCurrentWeek = () => api.get('/calendar/current-week');

// Season management
api.getCurrentSeason = () => api.get('/calendar/current-season');
api.getAvailableSeasons = () => api.get('/calendar/available-seasons');
api.triggerSeasonRollover = () => api.post('/calendar/trigger-rollover');

// Admin season override
api.getAdminSeasonOverride = () => api.get('/admin/override-season');
api.setAdminSeasonOverride = (season) => api.post('/admin/override-season', { season });
api.clearAdminSeasonOverride = () => api.delete('/admin/override-season');
api.triggerAdminRollover = () => api.post('/admin/trigger-rollover');
api.getSeasonRolloverStatus = () => api.get('/admin/season/rollover-status');
api.rearchiveSeason = (season) => api.post(`/admin/season/rearchive/${season}`);

// Picks and locks
api.fetchLocksForWeek = (week) => api.get(`/picks/week/${week}/all`);
api.updateMaxPicks = (season, week, maxPicks) =>
    api.put('/calendar/update-max-picks', { season, week, maxPicks });
api.fetchUserSeasonLocks = () => api.get('/picks/season-locks');

// Lock Overview
api.getLockOverview = (season, week, user, viewMode) =>
    api.get(`/lock-overview?season=${season}&week=${week}&user=${user}&viewMode=${viewMode}`);

// Calendar management
api.createCustomWeek = (season, week, startDate, endDate, notes, maxPicks) =>
    api.post('/calendar/create-week', { season, week, startDate, endDate, notes, maxPicks });
api.deleteCustomWeek = (season, week) =>
    api.delete('/calendar/delete-week', { data: { season, week } });

// User statistics
api.getActiveUserCount = () => api.get('/users/active-count');
api.getActiveUsers = () => api.get('/users/active');

// Historical user statistics
api.getHistoricalUserStats = (userId, season) => api.get(`/user-stats/historical/${userId}/${season}`);
api.getAllHistoricalStats = (season) => api.get(`/user-stats/historical/${season}`);
api.getUserHistoricalSeasons = (userId) => api.get(`/user-stats/user/${userId}`);
api.getAvailableHistoricalSeasons = () => api.get('/user-stats/seasons');
api.migrateUserStats = (season) => api.post(`/user-stats/migrate/${season}`);

// Database backup management
api.createBackup = () => api.post('/user-stats/backup');
api.listBackups = () => api.get('/user-stats/backups');
api.cleanupBackups = () => api.post('/user-stats/cleanup-backups');

// Socket management
let socket;

export const initSocket = () => {
  if (!socket) {
    const token = localStorage.getItem('token');
    if (!token) {
      return null;
    }

    // Extract socket URL logic to avoid nested ternary
    const getSocketUrl = () => {
      if (window.location.hostname === 'dev.lotwleague.com') {
        return 'https://54.161.78.25';  // Staging socket URL (via nginx HTTPS)
      }
      if (window.location.hostname === 'lotwleague.com' || window.location.hostname === 'www.lotwleague.com') {
        return 'https://api.lotwleague.com';  // Production socket URL
      }
      if (process.env.NODE_ENV === 'development') {
        return getDevSocketOrigin();
      }
      if (process.env.REACT_APP_SOCKET_URL) {
        return process.env.REACT_APP_SOCKET_URL;
      }
      return window.location.origin;
    };

    const socketUrl = getSocketUrl();

    socket = io(socketUrl, {
      transports: ['websocket'],
      withCredentials: true,
      autoConnect: false,
      auth: { token }
    });

    socket.on('connect', () => {
      // Socket connected successfully
    });

    socket.on('connect_error', async (error) => {
      console.error('Socket connection error:', error);
      if (error?.message === 'Authentication error: Invalid token') {
        try {
          const newToken = await refreshToken();
          socket.auth.token = newToken;
          socket.connect();
        } catch (refreshError) {
          console.error('Failed to refresh token for socket connection:', refreshError);
          disconnectSocket();
          if (!isPublicRoute(window.location.pathname)) {
            window.dispatchEvent(new CustomEvent('unauthorized'));
          }
        }
      }
    });

    socket.on('disconnect', (reason) => {
      if (reason === 'io server disconnect') {
        disconnectSocket();
        if (!isPublicRoute(window.location.pathname)) {
          window.dispatchEvent(new CustomEvent('unauthorized'));
        }
      }
    });

    socket.connect();
  }
  return socket;
};

export const getSocket = () => socket;

export const refreshToken = async () => {
  if (isRefreshing) {
    return new Promise((resolve) => {
      refreshSubscribers.push(resolve);
    });
  }

  isRefreshing = true;

  try {
    const currentToken = localStorage.getItem('token');
    if (!currentToken) {
      throw new Error('No token found');
    }

    const response = await axios.post(
      `${getBaseURL().replace(/\/$/, '')}/users/refresh-token`,
      {},
      {
        headers: {
          'Authorization': `Bearer ${currentToken}`
        },
        withCredentials: true
      }
    );

    if (!response?.data?.token) {
      throw new Error('Invalid response from refresh token endpoint');
    }

    const { token } = response.data;
    localStorage.setItem('token', token);

    refreshSubscribers.forEach(callback => callback(token));
    refreshSubscribers = [];

    return token;
  } catch (error) {
    console.error('Token refresh failed:', error);
    refreshSubscribers = [];
    localStorage.removeItem('token');

    if (!isPublicRoute(window.location.pathname)) {
      window.dispatchEvent(new CustomEvent('unauthorized'));
    }

    throw error;
  } finally {
    isRefreshing = false;
  }
};

export const disconnectSocket = () => {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
};

// Leaderboard endpoints
api.getLeaderboard = (season, visibilityState = null) => {
  const params = new URLSearchParams();
  if (season) params.append('season', season);
  if (visibilityState) {
    params.append('show_locks', visibilityState.show_locks);
    params.append('show_lockers', visibilityState.show_lockers);
  }
  return api.get(`/leaderboard?${params.toString()}`);
};
api.getLeaderboardOverview = (season) => {
  const url = season ? `/leaderboard/overview?season=${season}` : '/leaderboard/overview';
  return api.get(url);
};
api.getLeagueMetrics = (season) => {
  const url = season ? `/leaderboard/metrics?season=${season}` : '/leaderboard/metrics';
  return api.get(url);
};

// Add version endpoint
api.getServerVersion = () => api.get('/system/version');

// LLM Admin endpoints
api.getAvailableModels = () => api.get('/admin/llm/available-models');

// Playoff endpoints (user-facing)
api.getPlayoffStatus = async (season) => {
  return api.get(`/playoff/status/${season || ''}`);
};

api.submitPlayoffPrediction = async (playoffId, homeScore, awayScore) => {
  return api.post('/playoff/predictions/submit', {
    playoff_id: playoffId,
    home_score: homeScore,
    away_score: awayScore
  });
};

api.getPlayoffPredictions = async (playoffId) => {
  return api.get(`/playoff/predictions/${playoffId}`);
};

api.getPlayoffScoreGrid = async (playoffId) => {
  return api.get(`/playoff/score-grid/${playoffId}`);
};

api.getLeagueSettings = () => api.get('/league-settings');

api.getGameMatchupStats = (gameId) => api.get(`/games/${gameId}/matchup-stats`);

api.getGameLineHistory = (gameId) => api.get(`/games/${gameId}/line-history`);

export default api;


